/**
 * Animal bulk import — the contract both sides agree on
 * ============================================
 *
 * One producer hands RuralOps a `.csv`, `.xlsx`, PDF or photo of their herd
 * list; it becomes up to {@link ANIMAL_IMPORT_MAX_ROWS} animals in one atomic,
 * idempotent, entitlement-checked commit. This module is the shared vocabulary
 * of that flow: what the strict schema emits, what the client sends back, how a
 * herd name becomes a herd, and what a per-row complaint looks like.
 *
 * Pure — no IO, no dependency beyond the livestock enums.
 *
 * **The deliberate asymmetry.** The model emits `herdName` (free text, never an
 * identifier); the client sends `herdRef` (a reference into
 * {@link HerdMapping}). The review step is what maps names to references, which
 * is what makes an invented foreign key structurally impossible rather than
 * merely forbidden.
 *
 * That asymmetry is a statement about *directions*, not about withholding the
 * name: the review step cannot offer "your file says lote 'Recria 2024' — map it
 * to…" unless the name reaches the browser. It travels on the READ side only, as
 * {@link AnimalImportRowHerdNames} beside the candidate list, so the candidate
 * row itself — the shape the client echoes back to `validate`/`commit` — still
 * has no herd field but `herdRef`.
 */
// ============================================
// CAPS
// ============================================
/**
 * Rows per import. Equal to `LIVESTOCK_BATCH_MAX` on purpose: an import is a
 * bulk livestock write, and two different ceilings for "how many animals may one
 * request create" would be two different DoS guards to keep in step.
 *
 * In v1 this is a **spreadsheet** promise. Visual input is single-chunk
 * (Decision E) — a line range is meaningless for a PDF or a photo — so a
 * photographed list is capped at {@link ANIMAL_IMPORT_CHUNK_ROWS} and a longer
 * PDF is refused with a split-the-file message. The copy must say so.
 */
export const ANIMAL_IMPORT_MAX_ROWS = 500;
/**
 * Rows per chunk, and therefore per provider call. PROVISIONAL — the joint
 * `(chunk_rows, timeout, concurrency)` sweep replaces it with a measurement.
 *
 * Chunking buys **parallelism**, not merely timeout-fitting: 500 rows of output
 * is 500 rows of output however it is split, so splitting it finely and running
 * the pieces concurrently is what compresses the wall clock. Raising this to
 * halve the chunk count multiplies extraction wall clock at fixed concurrency.
 *
 * 20 because prompt v2 rows cost ~360 output tokens each (measured, below): a
 * 40-row chunk took 86 s, a 20-row chunk fits the 120 s per-call timeout with
 * room.
 *
 * KNOWN PROTOTYPE RISK: 25 chunks at the worker concurrency of 4 is 7 waves; at
 * the 120 s per-call timeout worst case that is 7 × 120 s = 840 s, past the
 * Lambda's 600 s budget. Typical chunks finish in about 45 s (≈ 315 s total), so
 * only a slow provider day on a full 500-row import trips it. Not mitigated here.
 */
export const ANIMAL_IMPORT_CHUNK_ROWS = 20;
/**
 * Chunks per import — `ceil(ANIMAL_IMPORT_MAX_ROWS / ANIMAL_IMPORT_CHUNK_ROWS)`,
 * stated as its own constant because the server validates an untrusted chunk
 * plan against it before writing any analysis row. An unvalidated chunk count is
 * a token-spend amplifier.
 */
export const ANIMAL_IMPORT_MAX_CHUNKS = 25;
/**
 * Output tokens one extracted row costs, used to derive the reason's
 * `max_tokens` budget. MEASURED with prompt v2 against claude-sonnet-5 (the bound
 * MEDIA_ANALYSIS model): a 40-row CSV chunk produced 12,921 output tokens, a
 * 29-line PDF 10,471 and a 27-line photo 8,628 — about 330-360 per row, because
 * every row carries the full `records` object whether filled or empty.
 *
 * The budget invariant this feeds (asserted by a unit test, so a chunk-size
 * change fails the build rather than truncating in production):
 *
 *   CSV:    ANIMAL_IMPORT_CHUNK_ROWS × ANIMAL_IMPORT_TOKENS_PER_ROW × 2 ≤ budget ≤ 16_000
 *   visual: ANIMAL_IMPORT_VISUAL_MAX_ROWS × ANIMAL_IMPORT_TOKENS_PER_ROW ≤ budget
 *
 * CSV chunks keep the 2× headroom (20 × 360 × 2 = 14_400). A PDF or photo is one
 * call with no chunking, so it runs at 1× (40 × 360 = 14_400): the measured cost
 * is already the tail case there, and 2× would need a streaming call.
 */
export const ANIMAL_IMPORT_TOKENS_PER_ROW = 360;
/**
 * Largest `acquisitionCost` an imported animal may carry — the **money column's**
 * domain, not a domain opinion about cattle prices.
 *
 * Every ledger amount is persisted as `numeric(14,2)`, i.e. 12 integer digits and
 * 2 fractional, and the ledger's own `formatNumberToAmount` THROWS on anything
 * wider (`money.ts` validates `/^-?\d{1,12}(\.\d{1,2})?$/` after `toFixed(2)`).
 * So `999_999_999_999.99` is the largest representable amount and `1e12` is the
 * first value that cannot be written at all.
 *
 * The bound exists because a producer's spreadsheet reaches it by accident: a
 * CPF/CNPJ or an internal id landing in a cost column is 11–14 digits. Without it
 * that row throws from INSIDE the commit transaction, after animals are already
 * inserted — atomicity holds, but the producer gets an opaque 500 instead of a
 * per-cell complaint. Enforced twice, deliberately: the route schema refuses it at
 * the edge (a 400, never a 500) and
 * `AnimalImportDiagnosticCode.COST_OUT_OF_RANGE` names the offending row for
 * every path that does not come through the schema — `commit` included, which is
 * the one that must never reach the throw.
 *
 * Rounding is covered by comparing against this value rather than against `1e12`:
 * `toFixed(2)` can only carry a number up to 13 integer digits if it is already
 * greater than `999_999_999_999.99`, so `value <= MAX` is exactly "formattable".
 */
export const ANIMAL_IMPORT_MAX_ACQUISITION_COST = 999_999_999_999.99;
/**
 * Lines a single-chunk (PDF / photo) extraction may emit, totals and summaries
 * included. Decoupled from {@link ANIMAL_IMPORT_CHUNK_ROWS}: a visual list cannot
 * be split, so its ceiling is the output budget, not the spreadsheet chunk size.
 */
export const ANIMAL_IMPORT_VISUAL_MAX_ROWS = 40;
/** Heading lines + the header row a canonical CSV may declare per chunk. */
export const ANIMAL_IMPORT_MAX_HEADER_LINES = 11;
/** Doses per animal a candidate may carry; the parser truncates, the route schema refuses more. */
export const ANIMAL_IMPORT_MAX_VACCINATIONS_PER_ANIMAL = 20;
/** Bounds of an imported current / purchase weight. */
export const ANIMAL_IMPORT_MIN_WEIGHT_KG = 1;
export const ANIMAL_IMPORT_MAX_WEIGHT_KG = 2000;
/** The record fields only a female can carry (`REPRODUCTION_ON_MALE`). */
export const ANIMAL_IMPORT_FEMALE_ONLY_FIELDS = [
    "reproductiveStatus",
    "breedingDate",
    "breedingMethod",
    "pregnancyDiagnosisDate",
    "expectedCalvingDate",
    "lastCalvingDate",
];
/** The record dates that may not be in the future (expected calving may). */
export const ANIMAL_IMPORT_PAST_RECORD_DATE_FIELDS = [
    "currentWeightDate",
    "breedingDate",
    "pregnancyDiagnosisDate",
    "lastCalvingDate",
];
export const ANIMAL_IMPORT_REPRODUCTIVE_STATUSES = [
    "PREGNANT",
    "EMPTY",
];
export const ANIMAL_IMPORT_BREEDING_METHODS = [
    "ARTIFICIAL_INSEMINATION",
    "NATURAL_BREEDING",
];
// ============================================
// DIAGNOSTICS
// ============================================
export var AnimalImportDiagnosticCode;
(function (AnimalImportDiagnosticCode) {
    AnimalImportDiagnosticCode["MISSING_TAG_ID"] = "MISSING_TAG_ID";
    AnimalImportDiagnosticCode["MISSING_SEX"] = "MISSING_SEX";
    AnimalImportDiagnosticCode["MISSING_BIRTH_DATE"] = "MISSING_BIRTH_DATE";
    AnimalImportDiagnosticCode["INVALID_ENUM_VALUE"] = "INVALID_ENUM_VALUE";
    AnimalImportDiagnosticCode["INVALID_DATE"] = "INVALID_DATE";
    AnimalImportDiagnosticCode["DATE_OUT_OF_RANGE"] = "DATE_OUT_OF_RANGE";
    AnimalImportDiagnosticCode["WEIGHT_OUT_OF_RANGE"] = "WEIGHT_OUT_OF_RANGE";
    /** Outside {@link ANIMAL_IMPORT_MAX_ACQUISITION_COST}, or negative. */
    AnimalImportDiagnosticCode["COST_OUT_OF_RANGE"] = "COST_OUT_OF_RANGE";
    AnimalImportDiagnosticCode["DUPLICATE_TAG_IN_PAYLOAD"] = "DUPLICATE_TAG_IN_PAYLOAD";
    AnimalImportDiagnosticCode["TAG_ALREADY_EXISTS"] = "TAG_ALREADY_EXISTS";
    AnimalImportDiagnosticCode["HERD_REF_UNRESOLVED"] = "HERD_REF_UNRESOLVED";
    AnimalImportDiagnosticCode["HERD_NOT_ON_ESTABLISHMENT"] = "HERD_NOT_ON_ESTABLISHMENT";
    AnimalImportDiagnosticCode["NON_ACTIVE_STATUS"] = "NON_ACTIVE_STATUS";
    /** A weight without the date it was taken (current weight, or purchase weight without an acquisition date). */
    AnimalImportDiagnosticCode["MISSING_WEIGHING_DATE"] = "MISSING_WEIGHING_DATE";
    /** Reproduction data (status, breeding, calving) on a male. */
    AnimalImportDiagnosticCode["REPRODUCTION_ON_MALE"] = "REPRODUCTION_ON_MALE";
})(AnimalImportDiagnosticCode || (AnimalImportDiagnosticCode = {}));
// ============================================
// THE MISSING-VALUE VOCABULARY
// ============================================
/**
 * §13a — "missing" is defined once, and both sides agree on it:
 *
 * - `null` — the model read the row and could not read this field. The normal case.
 * - `""` (and whitespace-only) — schema-valid because strict mode drops
 *   `minLength`, semantically identical to `null`, and produced by an empty
 *   source cell. Normalised at the boundary rather than propagated.
 * - **absent** (`undefined`) — cannot occur under strict tool use. It means the
 *   strict flag was lost, so it normalises to `null` here and the *caller* is
 *   what fails loudly: the diagnostic rules key on this normalisation, and the
 *   schema validation that runs before them is where an absent field is refused.
 *
 * The rules keying on the normalisation rather than on any one representation is
 * what stops a future provider change silently reclassifying an unreadable ear
 * tag as a valid one.
 *
 * Surrounding whitespace on a present value is stripped too — the same source
 * cell that produces `"  "` produces `" 1234 "`, and a tag id whose identity
 * depends on invisible padding is not an identity.
 */
export function normalizeMissingText(value) {
    if (value === null || value === undefined) {
        return null;
    }
    const trimmed = value.trim();
    return trimmed.length === 0 ? null : trimmed;
}
// ============================================
// LIFECYCLE — THE PRODUCER-FACING STATE
// ============================================
/**
 * What the producer sees an import as — the whole lifecycle, not the extraction
 * roll-up. The extraction roll-up (`AnimalImportExtractionStatusView`, backend)
 * answers "how far did the model get"; this answers "what can I do about it".
 * They are different vocabularies with overlapping-looking words, which is
 * exactly why the roll-up type carries `extraction` in its name.
 *
 * Folded from three inputs in a fixed precedence, implemented once in
 * `animalImportStatus.ts` and asserted by test:
 *
 * 1. cancelled → {@link AnimalImportStatus.CANCELLED}
 * 2. a receipt exists → {@link AnimalImportStatus.IMPORTED}
 * 3. roll-up `FAILED` → {@link AnimalImportStatus.NEEDS_ATTENTION}
 * 4. roll-up `COMPLETED` → {@link AnimalImportStatus.READY_FOR_REVIEW}
 * 5. otherwise → {@link AnimalImportStatus.EXTRACTING}
 *
 * Cancellation beating completion and a receipt beating a failed chunk are the
 * two orderings that are not self-evident: a producer who cancelled has said the
 * import is over, and a committed import is committed even if a later chunk read
 * badly.
 *
 * Const object + derived union rather than a TS `enum` (the house pattern — see
 * `MediaEntityType`), with **keys equal to their values** so a wire string is
 * usable as a lookup key by construction and `AnimalImportStatus[wire]` can
 * never be a partial map.
 */
export const AnimalImportStatus = {
    /** The model is still reading the file. Nothing to review yet. */
    EXTRACTING: "EXTRACTING",
    /** Every chunk read. The candidate list is waiting for the producer. */
    READY_FOR_REVIEW: "READY_FOR_REVIEW",
    /** A chunk failed; {@link ActiveAnimalImportSummary.blockingFailureCodes} says why. */
    NEEDS_ATTENTION: "NEEDS_ATTENTION",
    /** Terminal — animals were committed and a receipt exists. */
    IMPORTED: "IMPORTED",
    /** Terminal — the producer (or the TTL sweep) gave the farm's slot back. */
    CANCELLED: "CANCELLED",
};
//# sourceMappingURL=AnimalImport.js.map